import {
    EarnStrategyPositionExposureApi,
    EarnStrategyPositionItemApi,
    EarnStrategyPositionsResponseApi
} from "../api/earn-strategy-positions";
import {
    EarnStrategyPositionExposure,
    EarnStrategyPositionItem,
    EarnStrategyPositionsResponse
} from "../domain/earn-strategy-positions";

function parseOptionalBigInt(value: string | number | bigint | null | undefined): bigint | undefined {
    if (value === null || value === undefined) return undefined;
    if (typeof value === "bigint") return value;
    const normalizedValue = typeof value === "number" ? Math.trunc(value).toString() : value.split(".")[0];
    if (!normalizedValue) return undefined;
    try {
        return BigInt(normalizedValue);
    } catch {
        return undefined;
    }
}

function parseOptionalNumber(value: string | number | null | undefined): number | undefined {
    if (value === null || value === undefined) return undefined;
    const numberValue = typeof value === "number" ? value : Number(value);
    return Number.isFinite(numberValue) ? numberValue : undefined;
}

function parseOptionalAllocation(value: string | number | null | undefined): number | undefined {
    return parseOptionalNumber(value);
}

function parseEarnStrategyPositionExposureFromApi(api: EarnStrategyPositionExposureApi): EarnStrategyPositionExposure {
    return {
        mint: api.mint ?? undefined,
        symbol: api.symbol ?? undefined,
        lendingMarket:
            api.lendingMarket ??
            api.lending_market ??
            api.lendingMarketAddress ??
            api.lending_market_address ??
            undefined,
        reserveAddress:
            api.reserveAddress ??
            api.reserve_address ??
            api.reserve ??
            api.reservePubkey ??
            api.reserve_pubkey ??
            undefined,
        amountBase: parseOptionalBigInt(api.amountBase ?? api.amount_base),
        amountUsd: parseOptionalNumber(api.amountUsd ?? api.amount_usd)
    };
}

function parseEarnStrategyPositionItemFromApi(api: EarnStrategyPositionItemApi): EarnStrategyPositionItem {
    const earnVaultAddress =
        api.earnVaultAddress ?? api.earn_vault_address ?? api.vaultAddress ?? api.vault_address ?? api.address ?? "";
    const aumBase = parseOptionalBigInt(api.aumBase ?? api.aum_base);
    const amount = parseOptionalBigInt(
        api.amount ??
            api.amountAssets ??
            api.amount_assets ??
            api.lastObservedAmount ??
            api.last_observed_amount ??
            aumBase
    );
    const allocationBps = api.allocationBps ?? api.allocation_bps;
    const shareOfAum = parseOptionalAllocation(api.shareOfAum ?? api.share_of_aum);
    const allocation =
        parseOptionalAllocation(api.allocation) ??
        shareOfAum ??
        (allocationBps === null || allocationBps === undefined ? undefined : allocationBps / 10_000);
    const positionType = api.positionType ?? api.position_type ?? api.positionKind ?? api.position_kind ?? "Unknown";
    const rawPositionJson =
        api.rawPositionJson ?? api.raw_position_json ?? api.positionJson ?? api.position_json ?? api.position;
    const exposuresJson = api.exposuresJson ?? api.exposures_json ?? [];
    const mint = api.mint ?? api.assetMint ?? api.asset_mint ?? undefined;
    const aumUsd = parseOptionalNumber(api.aumUsd ?? api.aum_usd);

    return {
        earnVaultAddress,
        positionIndex: api.positionIndex ?? api.position_index ?? api.index ?? -1,
        positionType,
        positionKind: api.positionKind ?? api.position_kind ?? positionType,
        typeLabel: api.typeLabel ?? api.type_label ?? undefined,
        positionAddress:
            api.positionAddress ??
            api.position_address ??
            api.positionAccount ??
            api.position_account ??
            api.account ??
            (api.address && api.address !== earnVaultAddress ? api.address : ""),
        mint,
        symbol: api.symbol ?? undefined,
        assetMint: mint,
        aumBase,
        aumUsd,
        shareOfAum,
        amount,
        valueUsd: parseOptionalNumber(api.valueUsd ?? api.value_usd) ?? aumUsd,
        allocation,
        protocol: api.protocol ?? undefined,
        protocolName: api.protocolName ?? api.protocol_name ?? undefined,
        positionLabel: api.positionLabel ?? api.position_label ?? undefined,
        positionStatus: api.positionStatus ?? api.position_status ?? undefined,
        positionJson: rawPositionJson,
        rawPositionJson,
        exposuresJson: exposuresJson.map(parseEarnStrategyPositionExposureFromApi),
        slot: parseOptionalNumber(
            api.slot ??
                api.lastUpdatedSlot ??
                api.last_updated_slot ??
                api.observedSlot ??
                api.observed_slot ??
                api.snapshot_slot
        ),
        updatedAt: api.updatedAt ?? api.updated_at ?? api.createdAt ?? api.created_at ?? api.timestamp ?? undefined
    };
}

export function parseEarnStrategyPositionsResponseFromApi(
    api: EarnStrategyPositionsResponseApi | EarnStrategyPositionItemApi[] | null | undefined
): EarnStrategyPositionsResponse {
    if (!api) {
        return {
            items: [],
            pageInfo: {
                page: 0,
                pageSize: 0,
                totalItems: 0,
                totalPages: 0
            }
        };
    }

    const items = Array.isArray(api) ? api : api.items ?? [];
    const pageInfo = Array.isArray(api)
        ? {
              page: 0,
              pageSize: items.length,
              totalItems: items.length,
              totalPages: 1
          }
        : api.pageInfo ?? {
              page: 0,
              pageSize: items.length,
              totalItems: items.length,
              totalPages: 1
          };

    return {
        items: items.map(parseEarnStrategyPositionItemFromApi),
        pageInfo
    };
}
